import { ComponentPropsWithoutRef, PropsWithChildren, useMemo } from 'react';
import { IonButton } from '@ionic/react';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { Modals } from '../../exports/Enums';
import { selectOrderMethod, selectScheduledFor } from '../../../../store/slices/OrderSlice';
import useOrderPreparationTime from '../../../../hooks/order/useOrderPreparationTime';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import useModalRouter from '../../common/hooks/useModalRouter';
import HelperMethods from '../../../../services/exports/HelperMethods';
import useUserFriendlyScheduledFor from '../../common/hooks/useUserFriendlyScheduledFor';
import { selectCompany } from '../../../../store/slices/InitialDataSlice';

type DeliveryOptionsToolbarProps = PropsWithChildren & { className?: string };

const OrderMethodToolbar = ({ className, children }: DeliveryOptionsToolbarProps) => {
  const { openModal } = useModalRouter();
  const toolbarClassName = classNames(
    'border-white ion-color mx-auto w-fit px-sm py-[10px] bg-mobile-secondary/80 rounded-xl backdrop-blur-sm border-xs',
    className,
  );

  return (
    <div className={toolbarClassName}>
      <IonButton
        className="size-full [&::part(native)]:p-0 m-0 [&::part(native)]:!border-none !border-none"
        fill="clear"
        color="light"
        size="small"
        onClick={() => openModal({ id: Modals.OrderMethod })}
      >
        <div className="space-y-1">{children}</div>
      </IonButton>
    </div>
  );
};

type CompanyNameProps = ComponentPropsWithoutRef<'p'>;

OrderMethodToolbar.CompanyName = ({ className }: CompanyNameProps) => {
  const companyName = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.name);
  const cnClassName = classNames('text-xs font-medium leading-none', className);

  return <p className={cnClassName}>{companyName}</p>;
};

OrderMethodToolbar.OrderMethodDetails = ({ className }: CompanyNameProps) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Modals:OrderMethod:OrderMethodToolbar',
  });
  const cnClassName = classnames(
    'text-sm font-semibold flex justify-center items-center gap-x-1 leading-none',
    className,
  );
  const company = useSelector(selectCompany);
  const getPreparationTime = useOrderPreparationTime(company);
  const orderMethod = useSelector(selectOrderMethod);
  const scheduledFor = useSelector(selectScheduledFor);
  const formattedScheduledFor = useUserFriendlyScheduledFor({ scheduledFor });
  const labelText = useMemo(() => {
    if (!scheduledFor) {
      const waitingTime = HelperMethods.formatTimeInterval(getPreparationTime(orderMethod));

      return t('order_method_details.title', { orderMethod, waitingTime });
    }

    return `${t(`order_methods.${orderMethod}`)} ${formattedScheduledFor}`;
  }, [orderMethod, scheduledFor]);

  return (
    <p className={cnClassName}>
      <span className="first-letter:uppercase">{labelText}</span>
      <ChevronDownIcon className="size-4" />
    </p>
  );
};

export default OrderMethodToolbar;
